import React, { useEffect, useState } from "react";
import { authFetch, authFetchForm } from "../../api";
import { FiTrash2, FiPlus } from "react-icons/fi";
import IconPicker, { iconsToList } from "../IconPicker";

const inputClass = "w-full px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const emptyItem = { title: "", description: "", icons: "", order: 0, is_active: true };
// A narrower icon grid for the table cell (the shared picker's default is sized for a full-width form).
const compactPickerClass = "grid grid-cols-5 gap-1 max-h-44 overflow-y-auto p-1.5 bg-white/5 border border-navy-border rounded-md";
// Icon sets with long labels (seller services) get fewer, wider cells so the names stay readable.
const widePickerClass = "grid grid-cols-3 gap-1 max-h-64 overflow-y-auto p-1.5 bg-white/5 border border-navy-border rounded-md";

const toggleIconKey = (icons, key) => {
  const current = iconsToList(icons);
  return (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]).join(",");
};

// Manages a list of banner slides (image, title, description, icons, order, active). Used for the Home
// banner (services) and, with a different endpoint, for the Co-Work banner slider.
export default function HeroCarouselSettings({
  endpoint = "/core/services/",
  heading = "Home Banner",
  intro = null,
  deleteWarning = "Delete this service? It will disappear from both the Hero carousel and Our Services.",
  addLabel = "Add Service",
  itemLabel = "Service",
  iconSet = undefined,   // custom icon set for the picker (defaults to the developer-tools icons)
  iconHint = null,
}) {
  const [items, setItems] = useState([]);
  const [imageFiles, setImageFiles] = useState({}); // { [itemId]: File }
  const [error, setError] = useState("");
  const [newItem, setNewItem] = useState(emptyItem);
  const [newImageFile, setNewImageFile] = useState(null);

  const load = () => {
    authFetch(endpoint)
      .then((res) => res.json())
      .then((data) => setItems(data.sort((a, b) => a.order - b.order)))
      .catch(() => setError("Failed to load."));
  };

  useEffect(load, [endpoint]);

  const updateField = (id, field, value) => {
    setItems(items.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const buildFormData = (item) => {
    const fd = new FormData();
    fd.append("title", item.title);
    fd.append("description", item.description);
    fd.append("icons", item.icons);
    fd.append("order", item.order);
    fd.append("is_active", item.is_active);
    return fd;
  };

  const saveItem = async (item) => {
    setError("");
    const fd = buildFormData(item);
    if (imageFiles[item.id]) fd.append("image", imageFiles[item.id]);
    const res = await authFetchForm(`${endpoint}${item.id}/`, fd);
    if (!res.ok) {
      setError("Failed to save.");
      return;
    }
    setImageFiles((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    load();
  };

  const deleteItem = async (id) => {
    if (!window.confirm(deleteWarning)) return;
    await authFetch(`${endpoint}${id}/`, { method: "DELETE" });
    load();
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.title) return;
    setError("");
    const fd = buildFormData({ ...newItem, order: items.length + 1 });
    if (newImageFile) fd.append("image", newImageFile);
    const res = await authFetchForm(endpoint, fd, "POST");
    if (!res.ok) {
      setError("Failed to add.");
      return;
    }
    setNewItem(emptyItem);
    setNewImageFile(null);
    load();
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">{heading}</h1>
      <p className="text-sm text-gray-400 mb-6">
        {intro || (
          <>
            This same list also powers the "Our Services" section further down the Home page. In the Home Banner, the
            selected icons are shown as pills next to the title and description (no button there — the "Request Quote
            &amp; Information" button appears per-card in "Our Services" instead).
          </>
        )}
      </p>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3 w-28">Image</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 min-w-[300px]">{iconHint || "Icons"}</th>
              <th className="px-4 py-3 w-20">Order</th>
              <th className="px-4 py-3 w-20">Active</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const previewSrc = imageFiles[item.id] ? URL.createObjectURL(imageFiles[item.id]) : item.image;
              return (
                <tr key={item.id} className="border-t border-navy-border align-top">
                  <td className="px-4 py-3">
                    {previewSrc && (
                      <img src={previewSrc} alt={item.title} className="w-20 h-14 object-cover rounded-md border border-navy-border mb-1" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageFiles((prev) => ({ ...prev, [item.id]: e.target.files[0] }))}
                      className="text-xs text-gray-400 w-20"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      className={inputClass}
                      value={item.title}
                      onChange={(e) => updateField(item.id, "title", e.target.value)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <textarea
                      className={`${inputClass} min-h-[60px] resize-none`}
                      value={item.description}
                      onChange={(e) => updateField(item.id, "description", e.target.value)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <IconPicker
                      className={iconSet ? widePickerClass : compactPickerClass}
                      icons={iconSet}
                      selected={iconsToList(item.icons)}
                      onToggle={(key) => updateField(item.id, "icons", toggleIconKey(item.icons, key))}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      className={inputClass}
                      value={item.order}
                      onChange={(e) => updateField(item.id, "order", Number(e.target.value))}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={item.is_active}
                      onChange={(e) => updateField(item.id, "is_active", e.target.checked)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => saveItem(item)}
                        className="px-3 py-1 rounded-md bg-brand-gradient text-white text-xs font-semibold"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => deleteItem(item.id)}
                        className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10 flex items-center justify-center gap-1 text-xs"
                      >
                        <FiTrash2 size={14} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <form onSubmit={addItem} className="mt-6 bg-navy border border-navy-border rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">Add New {itemLabel}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">{itemLabel} Title</label>
            <input
              className={inputClass}
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              placeholder="e.g. Portfolio Websites"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">{iconHint || "Icons"}</label>
            <IconPicker
              className={iconSet ? widePickerClass : compactPickerClass}
              icons={iconSet}
              selected={iconsToList(newItem.icons)}
              onToggle={(key) => setNewItem({ ...newItem, icons: toggleIconKey(newItem.icons, key) })}
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">{itemLabel} Description</label>
          <textarea
            className={`${inputClass} min-h-[70px]`}
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Describe this service..."
          />
        </div>
        <div className="flex items-center gap-6 mt-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNewImageFile(e.target.files[0])}
              className="text-xs text-gray-400"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-4">
            <input
              type="checkbox"
              checked={newItem.is_active}
              onChange={(e) => setNewItem({ ...newItem, is_active: e.target.checked })}
            />
            Active
          </label>
          <button className="ml-auto mt-4 px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2">
            <FiPlus /> {addLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
